// Markdown for index cards: [[board links]], task checkboxes and $math$ (KaTeX, loaded on first use).
import MarkdownItFactory from "markdown-it";
import type { MarkdownIt, StateCore, StateInline, Token } from "markdown-it";

type Katex = { renderToString(tex: string, opts: Record<string, unknown>): string };
let katex: Katex | null = null;
let katexLoading: Promise<void> | null = null;
const katexListeners = new Set<() => void>();

export function needsMath(md: string): boolean {
  return /\$[^$\n]+\$|\$\$[\s\S]+?\$\$/.test(md);
}

export function loadKatex(): Promise<void> {
  if (katex) return Promise.resolve();
  if (!katexLoading) {
    katexLoading = Promise.all([import("katex"), import("katex/dist/katex.min.css")]).then(([mod]) => {
      katex = (mod as unknown as { default: Katex }).default ?? (mod as unknown as Katex);
      cache.clear();
      for (const l of katexListeners) l();
    });
  }
  return katexLoading;
}

export function onKatexReady(fn: () => void): () => void {
  katexListeners.add(fn);
  return () => katexListeners.delete(fn);
}

function renderMath(tex: string, display: boolean): string {
  if (!katex) return `<code class="math-pending">${escapeHtml(tex)}</code>`;
  try {
    return katex.renderToString(tex, { displayMode: display, throwOnError: false, output: "html", strict: "ignore" });
  } catch {
    return `<code>${escapeHtml(tex)}</code>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function wikiLinks(md: MarkdownIt): void {
  md.inline.ruler.before("link", "wikilink", (state: StateInline, silent: boolean) => {
    const src = state.src;
    const pos = state.pos;
    if (src.charCodeAt(pos) !== 0x5b || src.charCodeAt(pos + 1) !== 0x5b) return false;
    const end = src.indexOf("]]", pos + 2);
    if (end < 0) return false;
    const inner = src.slice(pos + 2, end);
    if (!inner.trim() || inner.includes("\n") || inner.length > 120) return false;
    if (!silent) {
      const [target, alias] = inner.split("|");
      const tok = state.push("wikilink", "", 0);
      tok.meta = { target: target.trim(), label: (alias ?? target).trim() };
    }
    state.pos = end + 2;
    return true;
  });
  md.renderer.rules.wikilink = (tokens: Token[], idx: number) => {
    const { target, label } = tokens[idx].meta as { target: string; label: string };
    return `<span class="wikilink" data-link="${escapeHtml(target)}">${escapeHtml(label)}</span>`;
  };
}

function math(md: MarkdownIt): void {
  md.inline.ruler.after("escape", "math_inline", (state: StateInline, silent: boolean) => {
    const src = state.src;
    const pos = state.pos;
    if (src[pos] !== "$") return false;
    const display = src[pos + 1] === "$";
    const open = display ? 2 : 1;
    const close = src.indexOf(display ? "$$" : "$", pos + open);
    if (close < 0 || close === pos + open) return false;
    const tex = src.slice(pos + open, close);
    if (!display && (/^\s|\s$/.test(tex) || /^\d/.test(src.slice(close + 1, close + 2)))) return false; // "$5 and $6" is money
    if (!silent) {
      const tok = state.push("math", "", 0);
      tok.content = tex;
      tok.meta = { display };
    }
    state.pos = close + open;
    return true;
  });
  md.renderer.rules.math = (tokens: Token[], idx: number) => renderMath(tokens[idx].content, !!(tokens[idx].meta as { display?: boolean } | null)?.display);
}

function tasks(md: MarkdownIt): void {
  md.core.ruler.after("inline", "tasks", (state: StateCore) => {
    let n = 0;
    for (let i = 2; i < state.tokens.length; i++) {
      const tok = state.tokens[i];
      if (tok.type !== "inline" || state.tokens[i - 2].type !== "list_item_open") continue;
      const m = /^\[([ xX])\](\s|$)/.exec(tok.content);
      if (!m || !tok.children?.length) continue;
      const first = tok.children[0];
      if (first.type !== "text") continue;
      first.content = first.content.replace(/^\[[ xX]\]\s?/, "");
      const box = new state.Token("html_inline", "", 0);
      const done = m[1] !== " ";
      box.content = `<span class="task${done ? " done" : ""}" data-task="${n++}"></span>`;
      tok.children.unshift(box);
      state.tokens[i - 2].attrJoin("class", done ? "task-item done" : "task-item");
    }
    return true;
  });
}

const md = new MarkdownItFactory({ html: false, linkify: true, breaks: true, typographer: true });
md.use(wikiLinks).use(math).use(tasks);
md.enable(["html_inline"]);
// external links are followed on double-tap; keep them inert in the canvas
const defaultLinkOpen = md.renderer.rules.link_open ?? ((t, i, o, _e, self) => self.renderToken(t, i, o));
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const href = tokens[idx].attrGet("href") || "";
  tokens[idx].attrSet("data-href", href);
  tokens[idx].attrSet("target", "_blank");
  tokens[idx].attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen(tokens, idx, options, env, self);
};

const cache = new Map<string, string>();

export function renderMarkdown(src: string): string {
  const hit = cache.get(src);
  if (hit !== undefined) return hit;
  // raw HTML is off, so only our own tags (tasks, links, KaTeX) reach the page
  const html = md.render(src);
  if (cache.size > 400) cache.delete(cache.keys().next().value!);
  cache.set(src, html);
  return html;
}

/** Toggle the n-th task checkbox in the markdown source. */
export function toggleTask(src: string, n: number): string {
  let i = -1;
  return src.replace(/^(\s*(?:[-*+]|\d+[.)])\s+)\[([ xX])\](?=\s|$)/gm, (whole, lead: string, mark: string) => {
    i++;
    if (i !== n) return whole;
    return `${lead}[${mark === " " ? "x" : " "}]`;
  });
}

/** First line of a card, without markdown symbols: used as its title. */
export function cardTitle(src: string): string {
  const line = src.split("\n").find((l) => l.trim()) || "";
  return line.replace(/^#+\s*/, "").replace(/[*_`~[\]]/g, "").trim().slice(0, 80);
}
